import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuth, REGISTRATION_RULES_API } from '../utils/api';

// Initial state for the context
const initialState = {
  isLoading: false,
  notification: null,
  hasUnsavedChanges: false,
  
  // Age Determination
  ageDeterminationMethod: 'january1',
  customDate: '01-01',
  
  // Entities
  ageGroups: [],
  styleCategories: [],
  competitionCategories: [],
  categoryGroups: [], // Global category groups (e.g., "Technical", "Performance")
  styleScoreSheets: [],
};

// Action types for the reducer
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  SET_AGE_DETERMINATION: 'SET_AGE_DETERMINATION',
  SET_CUSTOM_DATE: 'SET_CUSTOM_DATE',
  ADD_AGE_GROUP: 'ADD_AGE_GROUP',
  UPDATE_AGE_GROUP: 'UPDATE_AGE_GROUP',
  REMOVE_AGE_GROUP: 'REMOVE_AGE_GROUP',
  ADD_STYLE_CATEGORY: 'ADD_STYLE_CATEGORY',
  UPDATE_STYLE_CATEGORY: 'UPDATE_STYLE_CATEGORY',
  REMOVE_STYLE_CATEGORY: 'REMOVE_STYLE_CATEGORY',
  ADD_COMPETITION_CATEGORY: 'ADD_COMPETITION_CATEGORY',
  UPDATE_COMPETITION_CATEGORY: 'UPDATE_COMPETITION_CATEGORY',
  REMOVE_COMPETITION_CATEGORY: 'REMOVE_COMPETITION_CATEGORY',
  ADD_CATEGORY_GROUP: 'ADD_CATEGORY_GROUP',
  UPDATE_CATEGORY_GROUP: 'UPDATE_CATEGORY_GROUP',
  REMOVE_CATEGORY_GROUP: 'REMOVE_CATEGORY_GROUP',
  UPDATE_STYLE_SCORE_SHEETS: 'UPDATE_STYLE_SCORE_SHEETS',
  LOAD_SETTINGS: 'LOAD_SETTINGS',
  RESET_CHANGES: 'RESET_CHANGES',
};

// Reducer function
function registrationRulesReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ActionTypes.SET_NOTIFICATION:
      return { ...state, notification: action.payload };
    
    case ActionTypes.CLEAR_NOTIFICATION:
      return { ...state, notification: null };
    
    case ActionTypes.SET_AGE_DETERMINATION:
      return { 
        ...state, 
        ageDeterminationMethod: action.payload,
        hasUnsavedChanges: true
      };
    
    case ActionTypes.SET_CUSTOM_DATE:
      return { 
        ...state, 
        customDate: action.payload,
        hasUnsavedChanges: true
      };
    
    case ActionTypes.ADD_AGE_GROUP: {
      const updatedGroups = [...state.ageGroups, action.payload].sort((a, b) => {
        if (a.hasNoMinAge) return -1;
        if (b.hasNoMinAge) return 1;
        return a.minAge - b.minAge;
      });
      
      return {
        ...state,
        ageGroups: updatedGroups,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.UPDATE_AGE_GROUP: {
      const { index, ageGroup } = action.payload;
      const updatedGroups = [...state.ageGroups];
      updatedGroups[index] = ageGroup;
      
      // Sort after updating
      const sortedGroups = updatedGroups.sort((a, b) => {
        if (a.hasNoMinAge) return -1;
        if (b.hasNoMinAge) return 1;
        return a.minAge - b.minAge;
      });
      
      return {
        ...state,
        ageGroups: sortedGroups,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.REMOVE_AGE_GROUP: {
      const updatedGroups = [...state.ageGroups];
      updatedGroups.splice(action.payload, 1);
      
      return {
        ...state,
        ageGroups: updatedGroups,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.ADD_STYLE_CATEGORY:
      return {
        ...state,
        styleCategories: [...state.styleCategories, action.payload],
        hasUnsavedChanges: true
      };
    
    case ActionTypes.UPDATE_STYLE_CATEGORY: {
      const { index, styleCategory } = action.payload;
      const updatedCategories = [...state.styleCategories];
      updatedCategories[index] = styleCategory;
      
      return {
        ...state,
        styleCategories: updatedCategories,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.REMOVE_STYLE_CATEGORY: {
      const updatedCategories = [...state.styleCategories];
      updatedCategories.splice(action.payload, 1);
      
      return {
        ...state,
        styleCategories: updatedCategories,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.ADD_COMPETITION_CATEGORY:
      return {
        ...state,
        competitionCategories: [...state.competitionCategories, action.payload],
        hasUnsavedChanges: true
      };
    
    case ActionTypes.UPDATE_COMPETITION_CATEGORY: {
      const { index, competitionCategory } = action.payload;
      const updatedCategories = [...state.competitionCategories];
      updatedCategories[index] = competitionCategory;
      
      return {
        ...state,
        competitionCategories: updatedCategories,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.REMOVE_COMPETITION_CATEGORY: {
      const updatedCategories = [...state.competitionCategories];
      updatedCategories.splice(action.payload, 1);
      
      return {
        ...state,
        competitionCategories: updatedCategories,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.ADD_CATEGORY_GROUP:
      return {
        ...state,
        categoryGroups: [...state.categoryGroups, action.payload],
        hasUnsavedChanges: true
      };
    
    case ActionTypes.UPDATE_CATEGORY_GROUP: {
      const { index, categoryGroup } = action.payload;
      const updatedGroups = [...state.categoryGroups];
      updatedGroups[index] = categoryGroup;
      
      return {
        ...state,
        categoryGroups: updatedGroups,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.REMOVE_CATEGORY_GROUP: {
      const updatedGroups = [...state.categoryGroups];
      updatedGroups.splice(action.payload, 1);
      
      return {
        ...state,
        categoryGroups: updatedGroups,
        hasUnsavedChanges: true
      };
    }
    
    case ActionTypes.UPDATE_STYLE_SCORE_SHEETS:
      return {
        ...state,
        styleScoreSheets: action.payload,
        hasUnsavedChanges: true
      };
    
    case ActionTypes.LOAD_SETTINGS:
      return {
        ...state,
        ageGroups: action.payload.ageGroups || [],
        styleCategories: action.payload.styleCategories || [],
        competitionCategories: action.payload.competitionCategories || [],
        categoryGroups: action.payload.categoryGroups || [],
        styleScoreSheets: action.payload.styleScoreSheets || [],
        ageDeterminationMethod: action.payload.ageDeterminationMethod || 'january1',
        customDate: action.payload.customDate || '01-01',
        hasUnsavedChanges: false
      };
    
    case ActionTypes.RESET_CHANGES:
      return {
        ...state,
        ageGroups: action.payload.ageGroups || [],
        styleCategories: action.payload.styleCategories || [],
        competitionCategories: action.payload.competitionCategories || [],
        categoryGroups: action.payload.categoryGroups || [],
        styleScoreSheets: action.payload.styleScoreSheets || [],
        ageDeterminationMethod: action.payload.ageDeterminationMethod || 'january1',
        customDate: action.payload.customDate || '01-01',
        hasUnsavedChanges: false
      };
    
    default:
      return state;
  }
}

// Create the context
const RegistrationRulesContext = createContext();

// Helper function to sort age groups
const sortAgeGroups = (ageGroups) => {
  return (ageGroups || []).sort((a, b) => {
    if (a.hasNoMinAge) return -1;
    if (b.hasNoMinAge) return 1;
    return a.minAge - b.minAge;
  });
};

// Provider component
export function RegistrationRulesProvider({ children }) {
  const [state, dispatch] = useReducer(registrationRulesReducer, initialState);
  const queryClient = useQueryClient();
  
  // Use React Query to fetch settings
  const { data: settings, isLoading: isQueryLoading, error: queryError } = useQuery({
    queryKey: ['registration-rules'],
    queryFn: async () => {
      const response = await fetchWithAuth(REGISTRATION_RULES_API);
      if (response) {
        const sortedAgeGroups = sortAgeGroups(response.ageGroups);
        return {
          ...response, 
          ageGroups: sortedAgeGroups
        };
      }
      return null;
    }
  });
  
  // Use React Query for saving settings
  const { mutate: saveSettingsMutation, isPending: isSaving } = useMutation({
    mutationFn: async (data) => {
      return await fetchWithAuth(REGISTRATION_RULES_API, {
        method: 'PUT',
        body: data
      });
    },
    onSuccess: () => {
      // Invalidate and refetch the settings
      queryClient.invalidateQueries({ queryKey: ['registration-rules'] });
      dispatch({
        type: ActionTypes.SET_NOTIFICATION,
        payload: {
          type: 'success',
          message: 'Settings saved successfully!'
        }
      });
      dispatch({ 
        type: ActionTypes.RESET_CHANGES, 
        payload: state 
      });
    },
    onError: (error) => {
      dispatch({
        type: ActionTypes.SET_NOTIFICATION,
        payload: {
          type: 'error',
          message: 'Failed to save settings: ' + (error.message || 'Unknown error')
        }
      });
    }
  });
  
  // Update local state when React Query data changes
  useEffect(() => {
    if (settings) {
      dispatch({ type: ActionTypes.LOAD_SETTINGS, payload: settings });
    }
  }, [settings]);
  
  // Set error notification if query fails
  useEffect(() => {
    if (queryError) {
      dispatch({
        type: ActionTypes.SET_NOTIFICATION,
        payload: {
          type: 'error',
          message: 'Failed to load settings: ' + (queryError.message || 'Unknown error')
        }
      });
    }
  }, [queryError]);
  
  // Update loading state
  useEffect(() => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: isQueryLoading || isSaving });
  }, [isQueryLoading, isSaving]);
  
  // Load settings from the API
  const loadSettings = async () => {
    // This will trigger a refetch with React Query
    queryClient.invalidateQueries({ queryKey: ['registration-rules'] });
  };
  
  // Save settings to the API
  const saveSettings = async () => {
    // Make sure each age group has an explicit isCompetitive value
    const processedAgeGroups = state.ageGroups.map(group => ({
      ...group,
      isCompetitive: group.isCompetitive === true
    }));
    
    saveSettingsMutation({
      ageGroups: processedAgeGroups,
      ageDeterminationMethod: state.ageDeterminationMethod,
      customDate: state.customDate,
      styleCategories: state.styleCategories,
      competitionCategories: state.competitionCategories,
      categoryGroups: state.categoryGroups,
      styleScoreSheets: state.styleScoreSheets
    });
  };
  
  // Reset settings to last saved state
  const resetSettings = async () => {
    if (settings) {
      dispatch({ type: ActionTypes.LOAD_SETTINGS, payload: settings });
      dispatch({
        type: ActionTypes.SET_NOTIFICATION,
        payload: {
          type: 'success',
          message: 'Settings reset to saved values.'
        }
      });
    } else {
      // Refetch settings if not available
      loadSettings();
    }
  };
  
  // Update style score sheets
  const updateStyleScoreSheets = (styleScoreSheets) => {
    dispatch({
      type: ActionTypes.UPDATE_STYLE_SCORE_SHEETS,
      payload: styleScoreSheets
    });
  };
  
  // Value to be provided by the context
  const contextValue = {
    state,
    dispatch,
    ActionTypes,
    loadSettings,
    saveSettings,
    resetSettings,
    updateStyleScoreSheets
  };
  
  return (
    <RegistrationRulesContext.Provider value={contextValue}>
      {children}
    </RegistrationRulesContext.Provider>
  );
}

// Custom hook to use the context
export function useRegistrationRules() {
  const context = useContext(RegistrationRulesContext);
  if (!context) {
    throw new Error('useRegistrationRules must be used within a RegistrationRulesProvider');
  }
  return context;
} 